import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';

import { colors, spacing, typo, paperSx, primaryButtonSx, secondaryButtonSx } from '../theme/tokens';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Inline error boundary scoped to a single route / lazy chunk. Catches two
 * common cases the global ErrorBoundary doesn't handle well:
 *
 *   1. Lazy import failures after a deploy — the browser has an old chunk
 *      hash that no longer exists on the server. Default browser behavior
 *      is a cryptic "Loading chunk X failed" error; we surface a clean
 *      "refresh to get the latest" CTA.
 *
 *   2. Per-page render errors — keeps the app chrome (sidebar, header)
 *      visible so the user can navigate away rather than having the whole
 *      shell replaced by the top-level ErrorBoundary.
 */
class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    if (!hasError) return this.props.children;

    const msg = error?.message || '';
    // Heuristic: React's lazy() throws with a "Loading chunk" / "Failed to
    // fetch dynamically imported module" signature when a deploy has
    // invalidated hashed bundle filenames. Show a specific recovery UI.
    const isChunkError =
      /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/.test(
        msg,
      );

    return (
      <Box
        role="alert"
        sx={{ display: 'flex', justifyContent: 'center', py: spacing.xxxl, px: spacing.xl }}
      >
        <Paper elevation={0} sx={{ ...paperSx, maxWidth: 560, width: '100%', mb: 0 }}>
          <Typography sx={{ ...typo.sectionTitle, mb: spacing.md }}>
            {isChunkError ? 'A newer version is available' : 'Something went wrong on this page'}
          </Typography>
          <Typography sx={{ ...typo.bodyText, color: colors.textSecondary, mb: spacing.xl }}>
            {isChunkError
              ? 'The app has been updated since you opened this tab. Refresh to load the latest build.'
              : msg || 'An unexpected error occurred while rendering this page.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              disableElevation
              onClick={this.handleReload}
              sx={primaryButtonSx}
            >
              {isChunkError ? 'Refresh' : 'Reload page'}
            </Button>
            {!isChunkError && (
              <Button variant="outlined" onClick={this.handleReset} sx={secondaryButtonSx}>
                Try again
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default RouteErrorBoundary;
